import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { NativeBackButton, NativeDeepLinks } from './components/native/NativeShell';
import { initNativeShell } from './components/native/initNativeShell';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { androidChromeInsets } from './utils/androidChromeInsets';
import './index.css';

function readSafeAreaInset(edge: 'top' | 'bottom'): number {
  const probe = document.createElement('div');
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  const property = edge === 'top' ? 'paddingTop' : 'paddingBottom';
  probe.style[property] = `env(safe-area-inset-${edge}, 0px)`;
  document.body.appendChild(probe);
  const reported = parseFloat(getComputedStyle(probe)[property]) || 0;
  probe.remove();
  return reported;
}

function applyAndroidChromeInsets() {
  const win = window as Window & { Capacitor?: { getPlatform?: () => string } };
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  const isCapacitorAndroid = win.Capacitor?.getPlatform?.() === 'android';
  const isAndroidWebView =
    /Android/i.test(navigator.userAgent) && (isCapacitorAndroid || standalone);
  if (!isAndroidWebView) return;

  const insets = androidChromeInsets({
    reportedTop: readSafeAreaInset('top'),
    reportedBottom: readSafeAreaInset('bottom'),
    screenHeight: window.screen.height,
    innerHeight: window.innerHeight,
  });
  document.documentElement.style.setProperty('--app-safe-top', insets.top);
  document.documentElement.style.setProperty('--app-safe-bottom', insets.bottom);
}

void initNativeShell().finally(() => {
  applyAndroidChromeInsets();
  window.setTimeout(applyAndroidChromeInsets, 300);
});
window.addEventListener('orientationchange', () => {
  window.setTimeout(applyAndroidChromeInsets, 300);
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <NativeBackButton />
      <NativeDeepLinks />
      <AuthProvider>
        <TenantProvider>
          <App />
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>
);